import React, { useMemo } from "react";
import { colors2, ButtonGhostCompact, SkeletonLoader, makeArrayFromLength } from "oolib";
import { UI_TAG_DF } from "oolib/dist/v2/components/Typo2";
import { parkedSubtopicId } from "@okf/ootils";
import { useGetAnnotationsLazy } from "@/utils/react-query-hooks/getData";
import { useElemInView } from "@/utils/customHooks/useElemInView";
import { useAggrMetricsAcrossTimeAndDimension } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetLineChart/query/useAggrMetricsAcrossTimeAndDimension";
import { TagDetailSidebar, SectionLabel } from "@/_self_managed/SM_Dashboard/LensesTab/comps/TagDetailSidebar";
import { RowAnnosPanelBody } from "@/components/analysisModules/TopicClusteringTreeView";
import { ObservationSparkline } from "@/components/analysisModules/TopicClusteringTreeView/CategorySparklines";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { topicNodeFilter, signalsFilter, signalIdsInCategory } from "./scopeFilters";

/**
 * The side panel a sub-topic row opens: the row's own trend line, an Ask AI
 * entry, and its annotations within the scope, paged twenty at a time as the
 * list scrolls. A self-contained unit — it owns both requests, so any
 * surface that can name a row and a scope (the Topics page, the Overview's
 * topic sidebar) can open it.
 *
 *   row            the sub-topic row (or a parked / unplaced pseudo row —
 *                  `isUnplaced`, `parkedOf`, `category`)
 *   topicNodeId    the topic the row sits in — the trend request is a
 *                  per-sub-topic series under that topic; null for the
 *                  tree-level unplaced row (no trend)
 *   activeFilters  the surface's scope; the row's node filter is appended
 *   topicClusteringLens, signalMetaById, period, dateFieldMap
 *   onClose, onAskAI(row)   (onAskAI optional)
 *   zIndex, rightOffset, closeOnOutsideClick   pass through to the shell
 *
 * Nothing is requested until a row is open (`enabled: !!row`); the
 * annotation pages load as the sentinel after the last card scrolls into
 * view — the listing pages' way.
 */
const PanelCardsSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
    {makeArrayFromLength(3).map((_, i) => (
      <SkeletonLoader key={i} style={{ height: "9rem" }} />
    ))}
  </div>
);

export const SubtopicRowPanel = ({
  row,
  topicNodeId = null,
  activeFilters,
  topicClusteringLens,
  signalMetaById,
  period,
  dateFieldMap,
  onClose,
  onAskAI = null,
  zIndex,
  rightOffset,
  closeOnOutsideClick,
}) => {
  const categoryTheme = useSignalCategories();
  const signal = row?.signalId ? signalMetaById[row.signalId] : null;
  const accent = signal?.category ? categoryTheme.accentOf(signal.category) : colors2.grey80;

  // The clicked node's annotations within the same scope. A parked row (a
  // topic's unplaced observations, per category) is the topic's
  // `__unplaced:<topicId>` pseudo sub-topic, narrowed to that category's
  // signals.
  const panelFilters = useMemo(() => {
    if (!row) return [];
    if (row.parkedOf) {
      return [
        ...activeFilters,
        topicNodeFilter(parkedSubtopicId(row.parkedOf), topicClusteringLens),
        ...(row.category ? [signalsFilter(signalIdsInCategory(signalMetaById, row.category))].filter(Boolean) : []),
      ];
    }
    return [...activeFilters, topicNodeFilter(String(row.nodeId), topicClusteringLens)];
  }, [row, activeFilters, topicClusteringLens, signalMetaById]);
  const annosQuery = useGetAnnotationsLazy({
    activeFilters: panelFilters,
    queryId: row ? `topicsV2:${row.nodeId}` : "topicsV2:none",
    queryOptions: { enabled: !!row },
  });
  // getAnnotations answers { data, count, skip } — `data` is the page's docs.
  const annos = useMemo(() => (annosQuery.data?.pages || []).flatMap((pg) => pg.data || []), [annosQuery.data]);
  const { observerRef } = useElemInView({
    callback: () => {
      if (annosQuery.hasNextPage && !annosQuery.isFetchingNextPage) annosQuery.fetchNextPage();
    },
    enabled: !!row && !!annosQuery.hasNextPage,
  });

  // The row's own line: the per-sub-topic series under its topic, within
  // the scope, at the period's interval — the same request shape as the
  // drilldown's sparklines, one row of it. Real sub-topics only; a parked
  // or unplaced pseudo row has no node the series could count.
  const hasTrend = !!row && !row.isUnplaced && !row.parkedOf && !!topicNodeId;
  const { data: seriesRows, isLoading: seriesLoading } = useAggrMetricsAcrossTimeAndDimension({
    contentType: "annotations",
    dateValuePathByContentType: dateFieldMap,
    timelineRange: period ? { from: period.from, to: period.to } : undefined,
    interval: period?.interval,
    activeFilters: hasTrend ? [...activeFilters, topicNodeFilter(String(row.nodeId), topicClusteringLens)] : [],
    vizCardId: `topicsV2:row:${row?.nodeId}`,
    dimensionValuePath: hasTrend ? { dimensionType: "topicClusteringNode", level: "subtopic", parentTopicNodeId: String(topicNodeId) } : null,
    queryOptions: { enabled: hasTrend && !!period?.interval && !!dateFieldMap },
  });
  const countsByBin = useMemo(() => {
    if (!hasTrend || !seriesRows) return null;
    const out = {};
    for (const r of seriesRows) {
      const d = (r.dimensions || []).find((x) => String(x.value) === String(row.nodeId));
      if (d) out[r.timeInterval] = d.count;
    }
    return out;
  }, [hasTrend, seriesRows, row]);

  return (
    <TagDetailSidebar
      isOpen={!!row}
      onClose={onClose}
      width={440}
      zIndex={zIndex}
      rightOffset={rightOffset}
      closeOnOutsideClick={closeOnOutsideClick}
      primaryColor={accent}
      overrideHeaderTitle={<UI_TAG_DF style={{ color: accent }}>{signal?.display || (row?.isUnplaced ? "UNPLACED" : "FINDING")}</UI_TAG_DF>}
      title={row?.label || ""}
      description={row?.description}
    >
      {hasTrend && period && seriesLoading && (
        <div style={{ marginBottom: 20 }}>
          <SectionLabel>Trend</SectionLabel>
          <SkeletonLoader style={{ height: 72, width: "100%", opacity: 0.45, borderRadius: 4 }} />
        </div>
      )}
      {hasTrend && period && !seriesLoading && countsByBin && (
        <div style={{ marginBottom: 20 }}>
          <SectionLabel>Trend</SectionLabel>
          <ObservationSparkline countsByBin={countsByBin} period={period} color={accent} label={signal?.display || "observations"} height={72} />
        </div>
      )}
      {onAskAI && row && !row.isUnplaced && !row.parkedOf && (
        <div style={{ marginBottom: 12 }}>
          <ButtonGhostCompact S icon="Sparkle" value="Ask AI about this" onClick={() => onAskAI(row)} />
        </div>
      )}
      <SectionLabel>Observations ({row?.annotationCount ?? 0})</SectionLabel>
      {annosQuery.isLoading ? <PanelCardsSkeleton /> : <RowAnnosPanelBody annos={annos} status={annosQuery.status} showTags={!!row?.isUnplaced} />}
      {annosQuery.hasNextPage && <div ref={observerRef} style={{ height: 1 }} />}
      {annosQuery.isFetchingNextPage && <PanelCardsSkeleton />}
    </TagDetailSidebar>
  );
};
